import { AdvertisedPromotion } from '../../../../types/cart'

/**
 * Whether a listed offer can actually apply to the reservation as it stands.
 *
 * The advertised list is deliberately more permissive than the apply path.
 * `EligibleForAdvertisement` neutralises two checks before evaluating — it treats the
 * code as valid and clears the per-guest cap — so that an offer can be shown before the
 * booker has committed or identified anyone. Everything else (schedule, scope, blackout,
 * currency, global caps) is enforced identically to booking.
 *
 * The consequence is that a listed offer is not a promise. A promotion that caps
 * redemptions per guest is advertised at step 1, where no guest exists yet, and then
 * does not apply — which reads as the discount silently failing.
 *
 * The service says so: it sets `requiresGuestIdentity` on exactly those offers. This
 * turns that flag into the panel's answer to "will this actually work".
 */
export const offerNeedsGuestIdentity = (offer: AdvertisedPromotion): boolean =>
  offer.conditions?.requiresGuestIdentity === true

/**
 * Whether the panel can tell, at this step, that the offer will apply.
 *
 * Deliberately narrow: it reports only what the service has told us, and never guesses.
 * A code can still be refused later for reasons no client can see — already redeemed,
 * ambiguous across providers, a cap exhausted between listing and booking — so this
 * answers "is there a KNOWN reason it will not apply", not "is it guaranteed to work".
 * Presenting it as a guarantee would replace a silent failure with a confident wrong
 * promise, which is worse.
 */
export const knownBlockerFor = (
  offer: AdvertisedPromotion
): 'guest-identity' | null =>
  offerNeedsGuestIdentity(offer) ? 'guest-identity' : null
